import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Time } from 'types/general.types';
import { LoadingState } from 'types/enums';
import {
  ChatChannel,
  ChatChannelLinks,
  ChatChannelListItem,
  ChatChannelUser,
  ChatMessageItem,
  ChatMessageUnreadCountChangedPayload,
  CreateChatMessageValidation,
  EditChatMessageItem,
  MessageType,
} from 'types/chat.types';
import { createSongEffect } from 'app/store/SongList/effects';
import {
  createChatMessageEffect,
  deleteChatMessageEffect,
  fetchChatChannelMessagesEffect,
  fetchChatChannelsEffect,
  fetchChatUnreadCountEffect,
  fetchSongChannelEffect,
  updateLastSeenMessageEffect,
} from './effects';
import {
  formatMessageContent,
  toChannelMessageData,
  updateMessage,
} from './slice.utils';

export interface ChatState {
  currentChannel?: ChatChannel;
  isChatWindowOpened: boolean;
  isChannelsListOpen: boolean;
  messages: MessageType[];
  sentMessagesRefs: string[];
  createChatMessageLoadingState: LoadingState;
  createChatMessageValidation?: CreateChatMessageValidation;
  channels: ChatChannelListItem[];
  shouldUpdateChannel: boolean;
  _channelsLinks?: ChatChannelLinks;
  lastSeenMessageTime?: Time;
  songChannelLoadingState: LoadingState;
  hasUnreadMessages: boolean;
}

export const chatInitialState: ChatState = {
  currentChannel: undefined,
  isChatWindowOpened: false,
  isChannelsListOpen: false,
  messages: [],
  sentMessagesRefs: [],
  lastSeenMessageTime: undefined,
  createChatMessageLoadingState: LoadingState.PRISTINE,
  createChatMessageValidation: undefined,
  shouldUpdateChannel: false,
  channels: [],
  songChannelLoadingState: LoadingState.PRISTINE,
  hasUnreadMessages: false,
};

const slice = createSlice({
  name: 'chat',
  initialState: chatInitialState,
  reducers: {
    removeCurrentChannel(state) {
      state.currentChannel = undefined;
      state.messages = [];
    },
    addToMessages(state, { payload }: PayloadAction<MessageType>) {
      state.messages.push(payload);
    },
    removeFromMessages(state, { payload }: PayloadAction<string>) {
      const index = state.messages.findIndex(
        (message: MessageType) => message.guid === payload
      );
      if (index > -1) {
        state.messages.splice(index, 1);
      }
    },
    messageEditedAction(
      state,
      { payload }: PayloadAction<EditChatMessageItem>
    ) {
      state.messages = updateMessage(state.messages, payload);
    },
    messageReceivedAction(
      state,
      {
        payload: { message, shouldUpdateChannels },
      }: PayloadAction<{
        message: ChatMessageItem;
        shouldUpdateChannels?: boolean;
      }>
    ) {
      if (shouldUpdateChannels) {
        state.channels = state.channels.map((channel) => {
          if (channel.guid === message.channelGuid) {
            return {
              ...channel,
              latestMessage: toChannelMessageData(message, state),
              unreadMessageCount: channel.unreadMessageCount + 1,
            };
          }

          return channel;
        });
      }

      const { createdBy, channelGuid } = message;

      if (!createdBy || state.currentChannel?.guid !== channelGuid) {
        return;
      }

      const indexOfSentMessage = message.refId
        ? state.sentMessagesRefs.indexOf(message.refId)
        : -1;
      if (indexOfSentMessage !== -1) {
        state.sentMessagesRefs.splice(indexOfSentMessage, 1);
        return;
      }

      const { userGuid } = createdBy;
      const userCard = state.currentChannel?.channelUsers.find(
        (user: ChatChannelUser) => user.userGuid === userGuid
      );

      if (!userCard) {
        state.shouldUpdateChannel = true;
      }
      state.messages.push(formatMessageContent(message, userCard?.profileCard));
    },
    updateChatWindowAction(state, { payload }: PayloadAction<boolean>) {
      state.isChatWindowOpened = payload;
    },
    updateLastSeenMessageInChannelAction(
      state,
      { payload }: PayloadAction<Time | undefined>
    ) {
      if (state.currentChannel?.lastReadMessageDate) {
        if (payload) {
          state.currentChannel.lastReadMessageDate = payload;
        } else if (state.lastSeenMessageTime) {
          state.currentChannel.lastReadMessageDate = state.lastSeenMessageTime;
        }
        state.lastSeenMessageTime = undefined;
      }
    },
    updateHasUnreadMessages(
      state,
      { payload }: PayloadAction<ChatMessageUnreadCountChangedPayload>
    ) {
      if (payload.unreadMessageCount) {
        state.hasUnreadMessages = true;
      }

      if (state.currentChannel?.guid === payload.channelGuid) {
        state.currentChannel.unreadMessageCount = payload.unreadMessageCount;
      }
    },
    updateChannelsListOpen(state, { payload }: PayloadAction<boolean>) {
      state.isChannelsListOpen = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createSongEffect.fulfilled, (state, { payload }) => {
        if (payload.chat) {
          state.currentChannel = ({
            ...payload.chat,
          } as unknown) as ChatChannel;
        }
      })
      .addCase(fetchSongChannelEffect.pending, (state, { meta }) => {
        state.songChannelLoadingState = LoadingState.FETCHING;
        if (meta.arg.update) {
          return;
        }
        state.currentChannel = {
          guid: state.currentChannel?.guid,
          status: state.currentChannel?.status,
        } as ChatChannel;
        state.messages = [];
      })
      .addCase(fetchSongChannelEffect.fulfilled, (state, { payload, meta }) => {
        state.songChannelLoadingState = LoadingState.FETCHED;
        if (
          meta.arg.update &&
          state.currentChannel?.guid !== meta.arg.channelGuid
        ) {
          return;
        }
        state.shouldUpdateChannel = false;
        state.currentChannel = payload;
      })
      .addCase(fetchSongChannelEffect.rejected, (state) => {
        state.songChannelLoadingState = LoadingState.ERROR;
      })
      .addCase(createChatMessageEffect.pending, (state, { meta }) => {
        state.createChatMessageLoadingState = LoadingState.FETCHING;
        const { refId } = meta.arg;
        if (refId) {
          state.sentMessagesRefs.push(refId);
        }
      })
      .addCase(createChatMessageEffect.rejected, (state) => {
        state.createChatMessageLoadingState = LoadingState.ERROR;
      })
      .addCase(createChatMessageEffect.fulfilled, (state, { payload }) => {
        const { userGuid } = payload.createdBy;

        const userCard = state.currentChannel?.channelUsers.find(
          (user: ChatChannelUser) => user.userGuid === userGuid
        );
        if (userCard) {
          state.messages.push(
            formatMessageContent(payload, userCard.profileCard)
          );
        }
        state.createChatMessageLoadingState = LoadingState.FETCHED;

        if (state.currentChannel) {
          state.lastSeenMessageTime = {
            timestamp: Date.now(),
            iso: new Date(Date.now()).toISOString(),
          };
          state.currentChannel.unreadMessageCount = 0;
        }
      })
      .addCase(
        fetchChatChannelsEffect.fulfilled,
        (state, { payload, meta }) => {
          state.channels = meta.arg?.next
            ? state.channels.concat(payload.items)
            : payload.items;
          state._channelsLinks = payload._links;
        }
      )
      .addCase(deleteChatMessageEffect.fulfilled, (state, { payload }) => {
        state.messages = updateMessage(state.messages, payload);
      })
      .addCase(updateLastSeenMessageEffect.fulfilled, (state, { payload }) => {
        if (!payload) {
          return;
        }
        if (state.currentChannel) {
          state.lastSeenMessageTime = payload.lastReadMessageDate;
          state.currentChannel.unreadMessageCount = 0;
        }
      })
      .addCase(
        fetchChatChannelMessagesEffect.fulfilled,
        (state, { payload }) => {
          const formattedMessages: MessageType[] = [];

          payload.items.forEach((message) => {
            if (!message.createdBy) {
              return;
            }
            const { userGuid } = message.createdBy;

            const userCard = state.currentChannel?.channelUsers.find(
              (user: ChatChannelUser) => user.userGuid === userGuid
            );
            if (userCard) {
              formattedMessages.push(
                formatMessageContent(message, userCard.profileCard)
              );
            }
          });

          if (state.currentChannel) {
            state.currentChannel._links.next = payload?._links?.next;
          }

          if (formattedMessages.length === 0) {
            return;
          }

          if (payload.isNext) {
            state.messages.unshift(...formattedMessages);
          } else {
            state.messages = formattedMessages;
          }
        }
      )
      .addCase(fetchChatUnreadCountEffect.fulfilled, (state, { payload }) => {
        state.hasUnreadMessages = payload;
      });
  },
});

export const {
  removeCurrentChannel,
  addToMessages,
  removeFromMessages,
  messageReceivedAction,
  messageEditedAction,
  updateChatWindowAction,
  updateLastSeenMessageInChannelAction,
  updateHasUnreadMessages,
  updateChannelsListOpen,
} = slice.actions;

export const chatReducer = slice.reducer;
